import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import FundraisingService from "Lib/API/Fundraising";

const getOrgId = () => {
  try {
    const org = JSON.parse(localStorage.getItem("organization") || "{}");
    return org._id || org.id || null;
  } catch {
    return null;
  }
};

export const fetchCampaigns = createAsyncThunk(
  "fundraising/fetchCampaigns",
  async ({ token, status }, { rejectWithValue }) => {
    try {
      const res = await FundraisingService.getCampaigns(token, status, getOrgId());
      return res;
    } catch (err) {
      return rejectWithValue(err?.response?.data?.message || "Failed to load campaigns");
    }
  }
);

export const editUpdate = createAsyncThunk(
  "fundraising/editUpdate",
  async ({ campaignId, updateId, data, token }, { rejectWithValue }) => {
    try {
      return await FundraisingService.editUpdate(campaignId, updateId, data, token);
    } catch (err) {
      return rejectWithValue(err?.response?.data?.message || "Failed to edit update");
    }
  }
);

export const deleteUpdate = createAsyncThunk(
  "fundraising/deleteUpdate",
  async ({ campaignId, updateId, token }, { rejectWithValue }) => {
    try {
      return await FundraisingService.deleteUpdate(campaignId, updateId, token);
    } catch (err) {
      return rejectWithValue(err?.response?.data?.message || "Failed to delete update");
    }
  }
);

export const fetchCampaign = createAsyncThunk(
  "fundraising/fetchCampaign",
  async ({ id, token }, { rejectWithValue }) => {
    try {
      return await FundraisingService.getCampaignById(id, token);
    } catch (err) {
      return rejectWithValue(err?.response?.data?.message || "Failed to load campaign");
    }
  }
);

export const fetchAnalytics = createAsyncThunk(
  "fundraising/fetchAnalytics",
  async ({ id, token }, { rejectWithValue }) => {
    try {
      const res = await FundraisingService.getAnalytics(id, token);
      return res;
    } catch (err) {
      return rejectWithValue(err?.response?.data?.message || "Failed to load analytics");
    }
  }
);

export const createCampaign = createAsyncThunk(
  "fundraising/createCampaign",
  async ({ data, token }, { rejectWithValue }) => {
    try {
      const res = await FundraisingService.createCampaign({ ...data, org_id: getOrgId() }, token);
      return res;
    } catch (err) {
      return rejectWithValue(err?.response?.data?.message || "Failed to create campaign");
    }
  }
);

export const updateCampaign = createAsyncThunk(
  "fundraising/updateCampaign",
  async ({ id, data, token }, { rejectWithValue }) => {
    try {
      const res = await FundraisingService.updateCampaign(id, data, token);
      return res;
    } catch (err) {
      return rejectWithValue(err?.response?.data?.message || "Failed to update campaign");
    }
  }
);

export const updateCampaignStatus = createAsyncThunk(
  "fundraising/updateCampaignStatus",
  async ({ id, status, token }, { rejectWithValue }) => {
    try {
      const res = await FundraisingService.updateCampaignStatus(id, status, token);
      return res;
    } catch (err) {
      return rejectWithValue(err?.response?.data?.message || "Failed to update status");
    }
  }
);

export const postUpdate = createAsyncThunk(
  "fundraising/postUpdate",
  async ({ id, data, token }, { rejectWithValue }) => {
    try {
      const res = await FundraisingService.postUpdate(id, data, token);
      return res;
    } catch (err) {
      return rejectWithValue(err?.response?.data?.message || "Failed to post update");
    }
  }
);

export const fetchDonors = createAsyncThunk(
  "fundraising/fetchDonors",
  async ({ id, token, page }, { rejectWithValue }) => {
    try {
      const res = await FundraisingService.getDonors(id, token, page);
      return res;
    } catch (err) {
      return rejectWithValue(err?.response?.data?.message || "Failed to load donors");
    }
  }
);

const fundraisingSlice = createSlice({
  name: "fundraising",
  initialState: {
    campaigns: [],
    currentCampaign: null,
    analytics: null,
    donors: { donors: [], total: 0, page: 1 },
    loading: false,
    error: null,
  },
  reducers: {
    clearCurrentCampaign(state) {
      state.currentCampaign = null;
      state.analytics = null;
      state.donors = { donors: [], total: 0, page: 1 };
    },
    clearError(state) {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    const pending = (state) => { state.loading = true; state.error = null; };
    const rejected = (state, action) => { state.loading = false; state.error = action.payload; };

    builder
      .addCase(fetchCampaigns.pending, pending)
      .addCase(fetchCampaigns.fulfilled, (state, action) => {
        state.loading = false;
        state.campaigns = Array.isArray(action.payload) ? action.payload : [];
      })
      .addCase(fetchCampaigns.rejected, rejected)

      .addCase(fetchAnalytics.pending, pending)
      .addCase(fetchAnalytics.fulfilled, (state, action) => {
        state.loading = false;
        state.analytics = action.payload;
      })
      .addCase(fetchAnalytics.rejected, rejected)

      .addCase(createCampaign.pending, pending)
      .addCase(createCampaign.fulfilled, (state, action) => {
        state.loading = false;
        state.campaigns.unshift(action.payload);
      })
      .addCase(createCampaign.rejected, rejected)

      .addCase(updateCampaign.pending, pending)
      .addCase(fetchCampaign.fulfilled, (state, action) => {
        state.currentCampaign = action.payload;
      })
      .addCase(editUpdate.fulfilled, (state, action) => {
        state.currentCampaign = action.payload;
        if (state.analytics) state.analytics.updates = action.payload.updates || [];
      })
      .addCase(deleteUpdate.fulfilled, (state, action) => {
        state.currentCampaign = action.payload;
        if (state.analytics) state.analytics.updates = action.payload.updates || [];
      })

      .addCase(updateCampaign.fulfilled, (state, action) => {
        state.loading = false;
        const idx = state.campaigns.findIndex((c) => c._id === action.payload._id);
        if (idx !== -1) state.campaigns[idx] = action.payload;
        if (state.currentCampaign?._id === action.payload._id) {
          state.currentCampaign = action.payload;
        }
      })
      .addCase(updateCampaign.rejected, rejected)

      .addCase(updateCampaignStatus.fulfilled, (state, action) => {
        const idx = state.campaigns.findIndex((c) => c._id === action.payload._id);
        if (idx !== -1) state.campaigns[idx] = action.payload;
      })

      .addCase(postUpdate.fulfilled, (state, action) => {
        if (state.currentCampaign?._id === action.payload._id) {
          state.currentCampaign = action.payload;
        }
        if (state.analytics) {
          state.analytics.updates = action.payload.updates || [];
        }
      })

      .addCase(fetchDonors.pending, pending)
      .addCase(fetchDonors.fulfilled, (state, action) => {
        state.loading = false;
        state.donors = action.payload;
      })
      .addCase(fetchDonors.rejected, rejected);
  },
});

export const { clearCurrentCampaign, clearError } = fundraisingSlice.actions;
export default fundraisingSlice.reducer;
